import {
  ErrorMessage,
  Spinner,
  Button,
  SuccessPopup,
} from "@/common/components";
import { useContext, useState, useEffect } from "@/common/hooks";
import { PrivateContext } from "@/routes/PrivateRoute";
import {
  useGetContactInfoForProfileQuery,
  useUpdateContactInfoForProfileMutation,
} from "@/service/ProfileService";
import { useGetStatesForEnrollmentQuery } from "@/service/APIService";
import SecurityInformation from "@/components/Common/SecurityInformation";
import ContactInformation from "@/components/Common/ContactInformation";
import { scrollToTop } from "@/utils/Utilities";

const UpdateContactInformation = () => {
  const {
    debounceErrors,
    debounceState,
    labels,
    setNavHeader,
    cancel,
    setPageHeader,
  } = useContext(PrivateContext);

  const [securityInfo, setSecurityInfo] = useState({});
  const [contactInfo, setContactInfo] = useState({});
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [showPopup, setShowPopup] = useState(false);

  const {
    data: accessorData,
    isFetching,
    error,
  } = useGetContactInfoForProfileQuery();

  const {
    data: states,
    isFetching: isFetching1,
    error: error1,
  } = useGetStatesForEnrollmentQuery();

  const [updateContactInfoApi, { isLoading, error: error2 }] =
    useUpdateContactInfoForProfileMutation();

  useEffect(() => {
    if (accessorData) {
      const contactInfo = accessorData?.contact;

      setSecurityInfo({
        ...securityInfo,
        customUserId: accessorData?.accessor?.customUserId,
        challengeQuestion1: accessorData?.challengeQuestion1?.question,
        challengeQuestion1Answer: accessorData?.challengeQuestion1?.answer,
        challengeQuestion1ConfirmAnswer:
          accessorData?.challengeQuestion1?.answer,
        challengeQuestion2: accessorData?.challengeQuestion2?.question,
        challengeQuestion2Answer: accessorData?.challengeQuestion2?.answer,
        challengeQuestion2ConfirmAnswer:
          accessorData?.challengeQuestion2?.answer,
      });

      setContactInfo({
        ...contactInfo,
        firstName: contactInfo?.firstName,
        middleName: contactInfo?.middleName,
        lastName: contactInfo?.lastName,
        addressLine1: contactInfo?.addressLine1,
        addressLine2: contactInfo?.addressLine2,
        addressLine3: contactInfo?.addressLine3,
        city: contactInfo?.cityName,
        stateId: contactInfo?.stateCountry,
        zipCode: contactInfo?.zip?.replaceAll("-", ""),
        phoneNumber: contactInfo?.phoneNumber,
        phoneExt: contactInfo?.phoneExtension,
        cellPhoneNumber: contactInfo?.cellPhoneNumber,
        faxNumber: contactInfo?.faxPhoneNumber,
        email: contactInfo?.emailAddress,
        confirmEmail: contactInfo?.emailAddress,
      });
    }
  }, [accessorData]);

  const updateContactInfo = () => {
    const accessor = accessorData?.accessor;
    const contact = accessorData?.contact;
    const challengeQuestion1 = accessorData?.challengeQuestion1;
    const challengeQuestion2 = accessorData?.challengeQuestion2;

    const payload = {
      confirmEmail: contactInfo.confirmEmail,
      accessor: {
        customUserId: securityInfo.customUserId,
        lastMntOpId: accessor?.lastMntOpId,
        lastMntDateTime: accessor?.lastMntDateTime,
      },
      challengeQuestion1: {
        accessorChallengeQuestionId:
          challengeQuestion1?.accessorChallengeQuestionId,
        question: securityInfo.challengeQuestion1,
        answer: securityInfo.challengeQuestion1Answer,
        confirmAnswer: securityInfo.challengeQuestion1ConfirmAnswer,
        lastMntOpId: challengeQuestion1?.lastMntOpId,
        lastMntDateTime: challengeQuestion1?.lastMntDateTime,
      },
      challengeQuestion2: {
        accessorChallengeQuestionId:
          challengeQuestion2?.accessorChallengeQuestionId,
        question: securityInfo.challengeQuestion2,
        answer: securityInfo.challengeQuestion2Answer,
        confirmAnswer: securityInfo.challengeQuestion2ConfirmAnswer,
        lastMntOpId: challengeQuestion2?.lastMntOpId,
        lastMntDateTime: challengeQuestion2?.lastMntDateTime,
      },
      contact: {
        firstName: contactInfo?.firstName,
        middleName: contactInfo?.middleName,
        lastName: contactInfo?.lastName,
        addressLine1: contactInfo?.addressLine1,
        addressLine2: contactInfo?.addressLine2,
        addressLine3: contactInfo?.addressLine3,
        cityName: contactInfo.city,
        stateCountry: contactInfo.stateId,
        zip: contactInfo.zipCode.replaceAll("-", ""),
        phoneNumber: contactInfo.phoneNumber,
        phoneExtension: contactInfo.phoneExt,
        cellPhoneNumber: contactInfo.cellPhoneNumber,
        faxPhoneNumber: contactInfo.faxNumber,
        emailAddress: contactInfo?.email,
        lastMntOpId: contact?.lastMntOpId,
        lastMntDateTime: contact?.lastMntDateTime,
      },
    };

    updateContactInfoApi(payload)
      .unwrap()
      .then((res) => {
        scrollToTop();
        if (!res?.inError) {
          setShowPopup(true);
        }
      });
  };

  useEffect(() => {
    setNavHeader(labels?.navbar?.profile);
    setPageHeader(labels?.updateContactInformation?.title);
  }, []);

  const commonProps = {
    errors,
    setErrors,
    touched,
    setTouched,
    debounceState,
    debounceErrors,
    labels: labels.updateContactInformation,
  };

  return (
    <section className="container-medium py-5">
      {(isFetching || isFetching1 || isLoading) && <Spinner />}
      <ErrorMessage error={error || error1 || error2} />
      <SecurityInformation
        securityInfo={securityInfo}
        setSecurityInfo={setSecurityInfo}
        encryptData={true}
        {...commonProps}
      />
      <ContactInformation
        contactInfo={contactInfo}
        setContactInfo={setContactInfo}
        states={states}
        {...commonProps}
      />

      <div className="d-flex gap-1 flex-row justify-content-end mt-5">
        <Button
          id="cancel"
          text={labels?.updateContactInformation?.cancel}
          type="secondary"
          wrapperClassName="col-md-4 col-6"
          click={cancel}
        />
        <Button
          id="done"
          text={labels?.updateContactInformation?.done}
          wrapperClassName="col-md-4 col-6"
          click={updateContactInfo}
        />
      </div>

      <SuccessPopup
        showModal={showPopup}
        buttonText={labels?.updateContactInformation?.continue}
        click={cancel}
        body={labels?.updateContactInformation?.success}
      />
    </section>
  );
};

export default UpdateContactInformation;
